import { Injectable, inject, resource, computed, signal } from '@angular/core';
import { collection, query, where, getDocs, addDoc, updateDoc, doc, serverTimestamp, deleteDoc, limit } from 'firebase/firestore';
import { FIRESTORE_TOKEN } from '../firebase/firestore.provider';
import { AuthService } from '../auth/auth.service';
import { AccessLogService } from './access-log.service';
import { Organization, TemperatureThresholds, WbgtThresholds } from '../../shared/interfaces';

export interface OrganizationCreateInput {
  name: string;
  enabledFeatures: string[];
  wbgtThresholds?: WbgtThresholds | null;
  temperatureThresholds?: TemperatureThresholds | null;
}

export interface OrganizationUpdateInput {
  name?: string;
  enabledFeatures?: string[];
  wbgtThresholds?: WbgtThresholds | null;
  temperatureThresholds?: TemperatureThresholds | null;
}

@Injectable({
  providedIn: 'root'
})
export class OrganizationManageService {
  private readonly firestore = inject(FIRESTORE_TOKEN);
  private readonly authService = inject(AuthService);
  private readonly accessLogService = inject(AccessLogService);

  private readonly manualError = signal<string | null>(null);

  private async logAction(action: string, resourcePath: string, status: 'success' | 'error'): Promise<void> {
    const user = this.authService.user();
    if (!user) return;
    await this.accessLogService.addLog({
      userId: user.uid,
      userName: user.email || '',
      action,
      resource: resourcePath,
      status
    });
  }

  private readonly organizationsResource = resource({
    params: () => ({ role: this.authService.userRole() }),
    loader: async ({ params }) => {
      if (params.role !== 'super_admin') {
        return [];
      }
      try {
        const orgsRef = collection(this.firestore, 'organizations');
        const q = query(orgsRef, where('deletedAt', '==', null));
        const snapshot = await getDocs(q);
        return snapshot.docs
          .map(doc => ({
            id: doc.id,
            ...doc.data()
          } as Organization));
      } catch (err) {
        console.error('組織一覧の取得に失敗しました:', err);
        throw new Error('組織一覧の取得に失敗しました');
      }
    }
  });

  public readonly organizations = computed<Organization[]>(() => {
    return this.organizationsResource.hasValue() ? this.organizationsResource.value() : [];
  });

  public readonly isLoading = computed(() => 
    this.organizationsResource.status() === 'loading' || 
    this.organizationsResource.status() === 'reloading'
  );

  public readonly error = computed(() => {
    const manual = this.manualError();
    if (manual) return manual;
    const resErr = this.organizationsResource.error();
    if (resErr) return resErr instanceof Error ? resErr.message : '組織一覧の取得エラー';
    return null;
  });

  public reloadOrganizations(): void {
    this.manualError.set(null);
    this.organizationsResource.reload();
  }

  public async createOrganization(data: OrganizationCreateInput): Promise<string> {
    if (this.authService.userRole() !== 'super_admin') {
      throw new Error('権限がありません');
    }
    let status: 'success' | 'error' = 'success';
    let docId = '';
    try {
      this.manualError.set(null);
      const orgsRef = collection(this.firestore, 'organizations');
      const docRef = await addDoc(orgsRef, {
        ...data,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
        deletedAt: null
      });
      docId = docRef.id;
      return docId;
    } catch (err) {
      status = 'error';
      this.manualError.set('組織の作成に失敗しました');
      throw err;
    } finally {
      await this.logAction('organization.create', docId ? `organizations/${docId}` : 'organizations', status);
    }
  }

  public async updateOrganization(id: string, data: OrganizationUpdateInput): Promise<void> {
    if (this.authService.userRole() !== 'super_admin') {
      throw new Error('権限がありません');
    }
    let status: 'success' | 'error' = 'success';
    try {
      this.manualError.set(null);
      const orgRef = doc(this.firestore, 'organizations', id);
      await updateDoc(orgRef, {
        ...data,
        updatedAt: serverTimestamp()
      });
    } catch (err) {
      status = 'error';
      this.manualError.set('組織の更新に失敗しました');
      throw err;
    } finally {
      await this.logAction('organization.update', `organizations/${id}`, status);
    }
  }

  public async deleteOrganization(id: string): Promise<void> {
    if (this.authService.userRole() !== 'super_admin') {
      throw new Error('権限がありません');
    }
    let status: 'success' | 'error' = 'success';
    try {
      this.manualError.set(null);
      // 制約チェック: sites または departments または sensors が存在するか確認
      const sitesRef = collection(this.firestore, 'sites');
      const sitesQuery = query(sitesRef, where('organizationId', '==', id), where('deletedAt', '==', null), limit(1));
      const sitesSnapshot = await getDocs(sitesQuery);

      if (!sitesSnapshot.empty) {
        throw new Error('この組織に紐付く現場が存在するため削除できません');
      }

      const deptsRef = collection(this.firestore, 'site_management_departments');
      const deptsQuery = query(deptsRef, where('organizationId', '==', id), where('deletedAt', '==', null), limit(1));
      const deptsSnapshot = await getDocs(deptsQuery);

      if (!deptsSnapshot.empty) {
        throw new Error('この組織に紐付く管理部署が存在するため削除できません');
      }

      const sensorsRef = collection(this.firestore, 'sensors');
      const sensorsQuery = query(sensorsRef, where('organizationId', '==', id), where('deletedAt', '==', null), limit(1));
      const sensorsSnapshot = await getDocs(sensorsQuery);

      if (!sensorsSnapshot.empty) {
        throw new Error('この組織に紐付くセンサーが存在するため削除できません');
      }

      const orgRef = doc(this.firestore, 'organizations', id);
      await updateDoc(orgRef, { deletedAt: serverTimestamp() });
    } catch (err) {
      status = 'error';
      let errorMessage = '組織の削除に失敗しました';
      if (err instanceof Error && err.message.includes('削除できません')) {
        errorMessage = err.message;
      }
      this.manualError.set(errorMessage);
      throw err;
    } finally {
      await this.logAction('organization.delete', `organizations/${id}`, status);
    }
  }
}
